import type { Interior } from '../game/interior';
import { supabase } from './supabase';
import type { Look } from '../game/avatar';
import type { OceanData } from '../game/engine';
import type { TileEdit } from '../game/blocks';

// The class ocean's data. Rules live in supabase/migrations/20261010_ocean.sql;
// nothing here is a security boundary.

function check<T>(res: { data: T | null; error: { message: string } | null }): T {
  if (res.error) throw new Error(res.error.message);
  return res.data as T;
}

export async function fetchOcean(classroomId: string): Promise<OceanData> {
  const d = check(await supabase.rpc('ocean_world', { cid: classroomId })) as OceanData;
  // Tolerate an older ocean_world (before 20261016_no_spots.sql) instead of crashing.
  return { ...d, houses: d.houses ?? [], people: d.people ?? [] };
}

export async function saveLook(studentId: string, look: Look): Promise<void> {
  check(await supabase.from('student_avatars').upsert({ student_id: studentId, look, updated_at: new Date().toISOString() }));
}

/** Record an area discovered, a pearl collected, or a temple gate opened. */
export async function recordFind(classroomId: string, kind: 'area' | 'pearl' | 'gate', item: string): Promise<{ areas: string[]; pearls: string[] }> {
  return check(await supabase.rpc('ocean_found', { cid: classroomId, kind, item }));
}

// ── The sandbox ─────────────────────────────────────────────────────────────

/** The class's block changes; with `since`, only those after it (for keeping up with classmates). */
export async function fetchTiles(classroomId: string, since?: string): Promise<TileEdit[]> {
  let q = supabase.from('ocean_tiles').select('tx, ty, tile, student_id, updated_at').eq('classroom_id', classroomId).order('updated_at');
  if (since) q = q.gt('updated_at', since);
  return check(await q.limit(20000));
}

/** Mine a block: uses one use of the student's pickaxe. Returns the uses left. */
export async function mineBlock(classroomId: string, tx: number, ty: number, item: string): Promise<{ pickaxe: number }> {
  return check(await supabase.rpc('ocean_mine', { cid: classroomId, x: tx, y: ty, material: item }));
}

/** Place a block: uses one of that block and one use of the student's hammer. Returns the hammer's uses left. */
export async function placeBlock(classroomId: string, tx: number, ty: number, item: string): Promise<{ hammer: number }> {
  return check(await supabase.rpc('ocean_place', { cid: classroomId, x: tx, y: ty, block: item }));
}

export type Recipe = { id: string; kind: 'craft' | 'shop'; name: string; blurb: string; cost: number; inputs: Record<string, number>; output: string; output_qty: number; sort: number };

export async function fetchRecipes(): Promise<Recipe[]> {
  return check(await supabase.from('ocean_recipes').select('*').order('sort'));
}

export async function craft(classroomId: string, recipeId: string): Promise<{ balance: number }> {
  return check(await supabase.rpc('ocean_craft', { cid: classroomId, recipe: recipeId }));
}

export async function catchFish(classroomId: string, species: string): Promise<void> {
  check(await supabase.rpc('ocean_catch', { cid: classroomId, species }));
}

/** Put a house from your inventory anywhere in the ocean, standing at (x, y). */
export async function placeHouse(classroomId: string, kind: string, x: number, y: number): Promise<string> {
  return check(await supabase.rpc('ocean_place_house_at', { cid: classroomId, house: kind, hx: x, hy: y }));
}

/** Save the inside of your own house (see game/interior.ts), paying for what was added. Returns the new balance. */
export async function decorateHouse(houseId: string, interior: Interior): Promise<number> {
  return check(await supabase.rpc('ocean_decorate_house', { house: houseId, interior }));
}

/** What each decoration costs, by item id (ocean_decor_prices). */
export async function fetchDecorPrices(): Promise<Record<string, number>> {
  const rows: { id: string; cost: number }[] = check(await supabase.from('ocean_decor_prices').select('id, cost'));
  return Object.fromEntries(rows.map(r => [r.id, r.cost]));
}

/** A sword or trident hit a sea beast: uses one hit. Returns hits left. */
export async function useWeapon(classroomId: string, weapon: 'sword' | 'trident'): Promise<number> {
  return check(await supabase.rpc('ocean_use_weapon', { cid: classroomId, weapon }));
}

/** A sea beast took all three lives: lose up to 10 sand dollars. */
export async function faint(classroomId: string): Promise<{ lost: number; balance: number }> {
  return check(await supabase.rpc('ocean_faint', { cid: classroomId }));
}

/** A temple or Abyss chest's word question (from the student's flashcards), or null if it has none. */
export async function chestQuestion(classroomId: string, chest: string): Promise<{ term: string; lang: string; options: string[] } | null> {
  return check(await supabase.rpc('ocean_chest_question', { cid: classroomId, chest }));
}

/** Open a treasure chest (once per student), with the answer to its question if it asked one. */
export async function openChest(classroomId: string, chest: string, answer?: string): Promise<{ loot: { item: string; qty: number }[]; correct: boolean | null; answer: string | null }> {
  return check(await supabase.rpc('ocean_open_chest', { cid: classroomId, chest, answer: answer ?? null }));
}

/** Hand in an NPC's quest (game/npcs.ts). Returns the reward. */
export async function doQuest(classroomId: string, quest: string): Promise<Record<string, number>> {
  return check(await supabase.rpc('ocean_quest', { cid: classroomId, quest }));
}

/** A beast died (for the class's weekly goals). */
export async function recordDefeat(classroomId: string, kind: string): Promise<void> {
  check(await supabase.rpc('ocean_record_defeat', { cid: classroomId, kind }));
}

export type GoalKind = 'fish' | 'chests' | 'beasts' | 'kraken' | 'habitat' | 'quests';
export type Goal = { id: string; kind: GoalKind; target: number; reward: number; progress: number };

/** This week's class goals and how far the class has got (reached ones pay out as this is read). */
export async function goalStatus(classroomId: string): Promise<{ week_start: string; goals: Goal[] }> {
  return check(await supabase.rpc('ocean_goal_status', { cid: classroomId }));
}

/** Teacher: add a goal for this week. */
export async function addGoal(classroomId: string, weekStart: string, kind: GoalKind, target: number, reward: number): Promise<void> {
  check(await supabase.from('ocean_goals').insert({ classroom_id: classroomId, week_start: weekStart, kind, target, reward }));
}

export async function deleteGoal(id: string): Promise<void> {
  check(await supabase.from('ocean_goals').delete().eq('id', id));
}

/**
 * Visiting a habitat: the server counts the blocks placed around the beacon, and
 * if it's time, a fish moves in. Returns who lives there now and who just arrived.
 */
export async function attractFish(classroomId: string, tx: number, ty: number): Promise<{ type: string | null; residents: { id: string; species: string }[]; arrived: string | null }> {
  return check(await supabase.rpc('ocean_attract', { cid: classroomId, btx: tx, bty: ty }));
}

export async function pickUpHouse(houseId: string): Promise<void> {
  check(await supabase.rpc('ocean_pick_up_house', { house: houseId }));
}

/** Move a caught fish into one of your houses, or back to your bucket (null). */
export async function moveFish(fishId: string, houseId: string | null): Promise<void> {
  check(await supabase.rpc('ocean_move_fish', { fish: fishId, house: houseId }));
}

/** Let a fish that moved into your beacon home go (20261039). */
export async function releaseResident(id: string): Promise<void> {
  check(await supabase.rpc('ocean_release_resident', { resident: id }));
}

/** Let one of your fish go, for good (20261038). It stays in the Fishpedia. */
export async function releaseFish(fishId: string): Promise<void> {
  check(await supabase.rpc('ocean_release_fish', { fish: fishId }));
}

// ── Teacher: sand dollars and undoing building ─────────────────────────────

export type SandDollarAward = { id: string; classroom_id: string; student_id: string; amount: number; reason: string | null; created_at: string };

export async function awardSandDollars(classroomId: string, studentIds: string[], amount: number, reason: string | null): Promise<SandDollarAward[]> {
  return check(await supabase.rpc('award_sand_dollars', { cid: classroomId, students: studentIds, amount, why: reason ?? '' }));
}

export async function undoSandDollars(ids: string[]): Promise<void> {
  check(await supabase.rpc('undo_sand_dollars', { ids }));
}

export async function fetchSandDollarAwards(classroomId: string, limit = 50): Promise<SandDollarAward[]> {
  return check(await supabase.from('sand_dollar_awards').select('*').eq('classroom_id', classroomId).order('created_at', { ascending: false }).limit(limit));
}

/** Teacher: every student's balance in a class. */
export async function fetchWallets(classroomId: string): Promise<{ student_id: string; balance: number }[]> {
  return check(await supabase.from('ocean_wallets').select('student_id, balance').eq('classroom_id', classroomId));
}

/** Teacher: undo mining and building. `who` null = everyone; `since` null = all time. Returns how many blocks were reset. */
export async function resetTiles(classroomId: string, who: string | null, since: Date | null): Promise<number> {
  return check(await supabase.rpc('ocean_reset_tiles', { cid: classroomId, who, since: since?.toISOString() ?? null }));
}
